import React, { useState } from "react";
import { FaChevronRight, FaCubes, FaLayerGroup, FaUserTag } from "react-icons/fa";
import type { GrantedCollection } from "../../api/entitlementsAdminApi";

export interface GroupAsset {
  asset_id: number;
  /** null when the asset is not in the caller's own asset list. */
  asset_name: string | null;
  generation_subtype_code?: string | null;
  iso_code?: string | null;
}

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "done"; assets: GroupAsset[] };

interface Props {
  groups: GrantedCollection[];
  showRole: boolean;
  /** Assets of one group; called on the first expand of that group. */
  loadAssets: (collectionId: number) => Promise<GroupAsset[]>;
}

// Same look as the compact AG Grids beside it (AgGridTheme.css): lavender
// header, 44px rows, 14px Poppins, gray-300 cell borders. AG Grid's own
// master/detail rows are an Enterprise feature, so this is a plain table.
const HEADER_BG = "#E7E4FF";
const TH = "h-[50px] px-3 text-left font-bold text-[15px] text-gray-700 border-b border-r border-gray-300 last:border-r-0";
const TD = "h-11 px-3 border-b border-r border-gray-300 last:border-r-0";

const errorText = (e: unknown) => (e instanceof Error ? e.message : "Request failed");

/**
 * Asset groups granted to a role or user. Clicking a row (or its chevron)
 * expands it in place to list the group's assets; several can be open.
 */
const AssetGroupsTable: React.FC<Props> = ({ groups, showRole, loadAssets }) => {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [loads, setLoads] = useState<Record<number, LoadState>>({});
  const columns = showRole ? 4 : 3;

  const load = (collectionId: number) => {
    setLoads((prev) => ({ ...prev, [collectionId]: { status: "loading" } }));
    loadAssets(collectionId).then(
      (assets) => setLoads((prev) => ({ ...prev, [collectionId]: { status: "done", assets } })),
      (e) =>
        setLoads((prev) => ({ ...prev, [collectionId]: { status: "error", message: errorText(e) } })),
    );
  };

  const toggle = (key: string, collectionId: number) => {
    const opening = !open.has(key);
    setOpen((prev) => {
      const next = new Set(prev);
      if (opening) next.add(key);
      else next.delete(key);
      return next;
    });
    // Load on the first expand; a failed load is retried on the next expand.
    const state = loads[collectionId];
    if (opening && (!state || state.status === "error")) load(collectionId);
  };

  const detail = (collectionId: number) => {
    const state = loads[collectionId];
    if (!state || state.status === "loading") {
      return <p className="text-sm text-gray-500 italic">Loading assets...</p>;
    }
    if (state.status === "error") {
      return (
        <p role="alert" className="text-sm text-red-600">
          Couldn't load this group's assets: {state.message}
        </p>
      );
    }
    if (state.assets.length === 0) {
      return <p className="text-sm text-gray-500 italic">No assets in this group.</p>;
    }
    return (
      <table className="w-full text-sm bg-white border border-gray-200 rounded-lg overflow-hidden">
        <thead className="bg-gray-100 text-gray-600">
          <tr>
            <th className="px-3 py-1.5 text-left font-semibold">Asset</th>
            <th className="px-3 py-1.5 text-left font-semibold w-28">Asset ID</th>
            <th className="px-3 py-1.5 text-left font-semibold w-28">Type</th>
            <th className="px-3 py-1.5 text-left font-semibold w-28">ISO</th>
          </tr>
        </thead>
        <tbody>
          {state.assets.map((a) => (
            <tr key={a.asset_id} className="border-t border-gray-200">
              <td className="px-3 py-1.5">
                {a.asset_name ?? (
                  <span className="text-gray-400 italic" title="Not in your own asset list">
                    Not in your asset list
                  </span>
                )}
              </td>
              <td className="px-3 py-1.5">{a.asset_id}</td>
              <td className="px-3 py-1.5">{a.generation_subtype_code || ""}</td>
              <td className="px-3 py-1.5">{a.iso_code || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div
      className="flex-1 min-h-[280px] overflow-auto font-poppins text-sm text-gray-700"
      style={{ border: "1px solid #E7E4FF", borderRadius: "8px" }}
    >
      <table className="w-full border-collapse">
        <thead className="sticky top-0 z-10" style={{ backgroundColor: HEADER_BG }}>
          <tr>
            <th className={`${TH} w-12`} aria-label="Expand" />
            <th className={TH}>
              <span className="flex items-center gap-2">
                <FaLayerGroup className="text-gray-600" />
                Group
              </span>
            </th>
            <th className={`${TH} w-36`}>
              <span className="flex items-center gap-2">
                <FaCubes className="text-gray-600" />
                Assets
              </span>
            </th>
            {showRole && (
              <th className={TH}>
                <span className="flex items-center gap-2">
                  <FaUserTag className="text-gray-600" />
                  Role
                </span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {groups.map((g, i) => {
            const key = `${g.collection_id}-${g.via_role}-${i}`;
            const isOpen = open.has(key);
            return (
              <React.Fragment key={key}>
                <tr
                  data-testid="group-row"
                  className="cursor-pointer bg-white hover:bg-gray-200 transition-colors duration-200"
                  onClick={() => toggle(key, g.collection_id)}
                >
                  <td className={`${TD} text-center`}>
                    {/* No handler of its own: the click bubbles to the row. */}
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? "Collapse" : "Expand"} ${g.collection_name}`}
                      className="p-1 text-gray-600 hover:text-gray-800"
                    >
                      <FaChevronRight
                        className={`w-3 h-3 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
                      />
                    </button>
                  </td>
                  <td className={TD}>{g.collection_name}</td>
                  <td className={TD}>{g.asset_count}</td>
                  {showRole && <td className={TD}>{g.via_role}</td>}
                </tr>
                {isOpen && (
                  <tr data-testid="group-assets">
                    <td colSpan={columns} className="bg-gray-100 px-6 py-3 border-b border-gray-300">
                      {detail(g.collection_id)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AssetGroupsTable;
